import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import ENDPOINT from '../../../constants/endpoints';
import AsyncSelectPane from '../../../GbsUI/components/AsyncSelectPane';
import Checkbox from '../../../GbsUI/UI/Checkbox';
import RawButton from '../../../GbsUI/UI/RawButton';
import SegmentedRadio from '../../../GbsUI/UI/SegmentedRadio';
import TextInput from '../../../GbsUI/UI/TextInput';

const SignUpPage = ({history, state, updateState}) => {
  const formRef = useRef(null);
  const [linkedInHelpDetailed, setLinkedInHelpDetailed] = useState(false);

  const years = useMemo(() => {
    const years = [
      {
        label: 'Other',
        value: 0,
      }
    ];

    for (let i = -1; i < 2; i++) {
      const value = new Date().getFullYear() - i;

      years.push({
        value,
        label: value,
      });
    }

    return years.reverse();
  }, []);

  const firmStartYears = useMemo(() => {
    const years = [
      {
        label: 'Other',
        value: 0,
      }
    ];

    for (let i = -1; i < 2; i++) {
      const value = new Date().getFullYear() - i;

      years.push({
        value,
        label: value,
      });
    }

    return years.reverse();
  }, []);

  const onSubmit = useCallback((e) => {
    e.preventDefault();
    updateState({
      signUpFilled: true,
    });

    history.push('/student/questions');
  }, []);

  useEffect(() => {
    if (!state.loading) {
      if (!state.data.school) {
        history.replace('/student/school');
      }
    }
  }, [state]);

  useEffect(() => {
    updateState({
      signUpFilled: false,
      canContinue: true,
      onContinue: () => formRef.current.requestSubmit(),
    });
  }, []);

  return (
    <>
      <h1 className={'student-signup__title student-signup__title--small student-signup__title--left'}>CANDIDATE PROFILE</h1>
      <p className={'student-signup__description student-signup__description--left'}>To sign up, please summarize your background below</p>
      <h1 className={'student-signup__title'}></h1>
      <div className={'student-signup__content-wrap'}>
        <form ref={formRef} onSubmit={onSubmit} className={'student-form'}>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <TextInput
                name={'firstName'}
                value={state.data.firstName}
                label={'First Name'}
                placeholder={'e.g. John'}
                help={'Enter the name you commonly go by'}
                onChange={e => updateState({data: {firstName: e.target.value}})}
                required
              />
            </div>
            <div className={'student-form__col'}>
              <TextInput
                name={'lastName'}
                value={state.data.lastName}
                label={'Last Name'}
                placeholder={'e.g. Smith'}
                onChange={e => updateState({data: {lastName: e.target.value}})}
                required
              />
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <TextInput
                name={'email'}
                label={'Personal Email'}
                value={state.data.email}
                type={'email'}
                placeholder={'user@example.com'}
                help={'Enter a personal email where you can securely receive recruiting info (avoid work/school emails which may change).'}
                onChange={e => updateState({data: {email: e.target.value}})}
                required
              />
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <TextInput
                name={'phone'}
                label={'Phone Number'}
                value={state.data.phone}
                placeholder={''}
                help={'Used only to contact you about potential recruiting opportunities.'}
                onChange={e => updateState({data: {phone: e.target.value}})}
                required
              />
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <AsyncSelectPane
                name={'school'}
                label={'Undergraduate School'}
                title={'Change your school'}
                selected={state.data.school}
                onChange={v => updateState({data: {school: v}})}
                source={ENDPOINT.STUDENT.SCHOOLS}
                createNewTitle={'Can\'t find your school in the list?'}
                createNewSubtitle={'Select your University NOT your business school or program (select UPenn, not Wharton).'}
                createNewHelp={'If your University or College is not listed, you can manually add it above and press ENTER.'}
                createNewPlaceholder={'e.g. University of Virginia'}
                notFoundText={'School is not found'}
                searchPlaceholder={'Enter the school name'}
                help={'Select school where you completed or expect to complete Undergrad'}
                disclaimer={'Note: GoBuyside has no affiliation with these colleges and universities; names and logos are displayed for informational purposes and clarity.'}
                creatable
                required
                autoselect={true}
              />
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <SegmentedRadio
                name={'graduationYear'}
                label={'Graduation Year'}
                onChange={v => updateState({data: {graduationYear: v}})}
                options={years}
                checked={state.data.graduationYear}
                help={`Select your undergrad graduation year (or expected year). If you are completing a concurrent undergrad & grad degree (e.g. 5-year bachelor's plus masters), enter your year graduating from the combined program.`}
                required
              />
            </div>
          </div>
          {state.data.graduationYear === 0 && (
            <div className={'student-form__row'}>
              <div className={'student-form__col'}>
                <TextInput
                  type={'number'}
                  name={'graduationYearCustom'}
                  label={'What year did you / do you expect to finish undergrad?'}
                  value={state.data.graduationYearCustom}
                  placeholder={'e.g. 2022'}
                  min={1900}
                  max={new Date().getFullYear() + 10}
                  onChange={e => updateState({data: {graduationYearCustom: e.target.value}})}
                  required
                />
              </div>
            </div>
          )}
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <AsyncSelectPane
                name={'firm'}
                label={'Firm'}
                title={'Change your firm'}
                selected={state.data.firm}
                onChange={v => updateState({data: {firm: v}})}
                source={ENDPOINT.STUDENT.FIRMS}
                createNewTitle={'Can\'t find your firm in the list?'}
                createNewSubtitle={'Manually add your firm in the field below.'}
                createNewPlaceholder={'e.g. Goldman Sachs'}
                notFoundText={'Firm is not found'}
                searchPlaceholder={'Enter the firm name'}
                help={'Indicate the firm you have joined or are joining. Full-time ongoing employment offers only - no internships or temp roles.'}
                defaultOption={{
                  name: 'Have yet to accept a full-time position',
                  value: 'HAVE_NOT_ACCEPTED_JOB'
                }}
                defaultOptionText={'Have yet to accept a full-time position'}
                disabled={state.data.firm && state.data.firm.value === 'HAVE_NOT_ACCEPTED_JOB'}
                creatable
                required
                autoselect={true}
                disclaimer={'Note: GoBuyside has no affiliation with these firms; names and logos are displayed for informational purposes and clarity.'}
              />
              <Checkbox
                className={'student-form__firm-checkbox'}
                onChange={(e) => {
                  updateState({
                    data: {
                      firmStartYearCustom: e.target.checked ? '' : state.data.firmStartYearCustom,
                      firmStartYear: e.target.checked ? '' : new Date().getFullYear(),
                      firm: e.target.checked ? {
                        name: 'Have yet to accept a full-time position',
                        value: 'HAVE_NOT_ACCEPTED_JOB'
                      } : null,
                    }
                  });
                }}
                label={'Have yet to accept a full-time position'}
                checked={!!state.data.firm && state.data.firm.value === 'HAVE_NOT_ACCEPTED_JOB'}
              />
            </div>
          </div>

          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <SegmentedRadio
                name={'firmStartYear'}
                label={'Firm start year'}
                help={'Enter the year you started or will be starting full time professional employment, post-undergrad.'}
                onChange={v => updateState({data: {firmStartYear: v}})}
                options={firmStartYears}
                checked={state.data.firmStartYear}
                disabled={state.data.firm && state.data.firm.value === 'HAVE_NOT_ACCEPTED_JOB'}
                required
              />
            </div>
          </div>
          {state.data.firmStartYear === 0 && (
            <div className={'student-form__row'}>
              <div className={'student-form__col'}>
                <TextInput
                  type={'number'}
                  name={'firmStartYearCustom'}
                  label={'What year did you / do you expect to start full-time employment?'}
                  value={state.data.firmStartYearCustom}
                  placeholder={'e.g. 2022'}
                  min={1900}
                  max={new Date().getFullYear() + 10}
                  onChange={e => updateState({data: {firmStartYearCustom: e.target.value}})}
                  required
                />
              </div>
            </div>
          )}
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <AsyncSelectPane
                name={'city'}
                label={'City'}
                title={'Change your current location'}
                selected={state.data.city}
                onChange={v => updateState({data: {city: v}})}
                source={ENDPOINT.STUDENT.CITIES}
                notFoundText={'City is not found'}
                searchPlaceholder={'Enter the city name'}
                help={'Please list the city where you primarily live (or will be living if you are moving.)'}
                showIcon={false}
                autoselect
                required
              />
            </div>
          </div>
          <div className={'student-form__row'}>
            <div className={'student-form__col'}>
              <TextInput
                name={'linkedin'}
                label={'LinkedIn Account (optional)'}
                help={'The correct format is https://www.linkedin.com/in/username/'}
                value={state.data.linkedInUrl}
                placeholder={'https://www.linkedin.com/in/username/'}
                pattern={'https://(www.)?linkedin.com/in/.+'}
                onChange={e => updateState({data: {linkedInUrl: e.target.value}})}
                onBlur={(e) => {
                  const val = e.target.value.trim();

                  if (val) {
                    try {
                      const url = new URL(val);

                      if (url.protocol !== 'https:') {
                        url.protocol = 'https:';
                      }

                      if (url.hostname !== 'www.linkedin.com') {
                        url.hostname = 'www.linkedin.com';
                      }

                      updateState({data: {linkedInUrl: url.toString()}})
                    } catch (e) {
                      const regex = /(www\.)?(linkedin.com)?\/?in\/([\w-]*)\/?/gm;
                      const match = regex.exec(val);

                      if (match) {
                        updateState({data: {linkedInUrl: `https://www.linkedin.com/in/${match[3]}`}});
                      } else {
                        updateState({data: {linkedInUrl: `https://www.linkedin.com/in/${val.replace('/', '')}`}});
                      }
                    }

                  }
                }}
              />
              <div className={'text-input__help'}>
                To find your LinkedIn profile link, log into LinkedIn, click on your profile, and copy the URL from the address bar of your browser.
                {' '}
                <RawButton onClick={() => setLinkedInHelpDetailed(!linkedInHelpDetailed)}>Need help?</RawButton>
                {linkedInHelpDetailed && (
                  <div className={'linkedin-help-steps'}>
                    <p>1. Log in to LinkedIn: Start by signing into your LinkedIn account.</p>
                    <p>2. Go to Your Profile: Click on your profile picture or name at the top of the LinkedIn homepage to go to your profile
                      page.</p>
                    <p>3. Locate Your LinkedIn URL: Once on your profile page, look for the URL in your browser's address bar. It typically looks like
                      https://www.linkedin.com/in/your-custom-url.</p>
                    <p>4. Copy Your Profile URL: Highlight the URL in the address bar, right-click it, and select "Copy" to copy your LinkedIn profile
                      link.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </>

  )
    ;
}

export default SignUpPage;
